// Shop by need finder: pick up to 3 needs, see products ranked by how many needs they cover,
// then narrow the results with the safety filters.
// Tiles are plain links in the markup; this element turns them into toggles, so the page still works without JS.

const MAX_NEEDS = 3;
const PAGE_SIZE = 12;

class HayaNeedFinder extends HTMLElement {
  connectedCallback() {
    this.selected = [];
    this.limit = PAGE_SIZE;
    this.tiles = [...this.querySelectorAll('[data-need]')];
    this.items = [...this.querySelectorAll('[data-finder-item]')];
    // Each filter checkbox's value names the item data attribute that must be "true" (pregnancy, children)
    this.filters = [...this.querySelectorAll('[data-finder-filter]')];
    this.el = (name) => this.querySelector(`[data-finder-${name}]`);

    this.indexItems();
    this.upgradeTiles();
    this.bindControls();
    this.querySelectorAll('[data-finder-enhanced]').forEach((node) => (node.hidden = false));
    this.restoreFromUrl();
  }

  indexItems() {
    const map = JSON.parse(this.el('map').textContent);
    this.items.forEach((item, order) => {
      item.order = order;
      item.needs = Object.keys(map).filter((need) => map[need].includes(item.dataset.handle));
    });
  }

  upgradeTiles() {
    this.tiles.forEach((tile) => {
      tile.setAttribute('role', 'button');
      tile.setAttribute('aria-pressed', 'false');
      tile.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(tile.dataset.need);
      });
      tile.addEventListener('keydown', (event) => {
        if (event.key !== ' ') return;
        event.preventDefault();
        this.toggle(tile.dataset.need);
      });
    });
  }

  bindControls() {
    this.el('info').addEventListener('keydown', (event) => {
      if (event.key === 'Escape') document.activeElement.blur();
    });
    this.el('submit').addEventListener('click', () => this.showResults(true));
    this.el('back').addEventListener('click', () => this.showPicker());
    this.el('more').addEventListener('click', () => {
      this.limit += PAGE_SIZE;
      this.renderResults();
    });
    this.filters.forEach((control) =>
      control.addEventListener('change', () => {
        this.limit = PAGE_SIZE;
        this.renderResults();
        this.syncUrl();
      })
    );
  }

  toggle(need) {
    const index = this.selected.indexOf(need);
    if (index >= 0) this.selected.splice(index, 1);
    else if (this.selected.length < MAX_NEEDS) this.selected.push(need);
    else return;
    this.syncPicker();
    this.syncUrl();
  }

  syncPicker() {
    const full = this.selected.length >= MAX_NEEDS;
    this.tiles.forEach((tile) => {
      const pressed = this.selected.includes(tile.dataset.need);
      tile.setAttribute('aria-pressed', String(pressed));
      tile.setAttribute('aria-disabled', String(full && !pressed));
    });
    this.el('counter').textContent = `${this.selected.length}/${MAX_NEEDS} selected`;
    this.el('submit').disabled = this.selected.length === 0;
  }

  showResults(moveFocus) {
    this.el('picker').hidden = true;
    this.el('results').hidden = false;
    this.limit = PAGE_SIZE;
    this.renderChips();
    this.renderResults();
    this.syncUrl();
    if (moveFocus) {
      this.el('heading').focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  showPicker() {
    this.el('results').hidden = true;
    this.el('picker').hidden = false;
    this.syncUrl();
    this.tiles.find((tile) => tile.getAttribute('aria-pressed') === 'true')?.focus();
  }

  passesFilters(item) {
    return this.filters.every((filter) => !filter.checked || item.dataset[filter.dataset.finderFilter] === 'true');
  }

  score(item) {
    if (!this.passesFilters(item)) return 0;
    return item.needs.filter((need) => this.selected.includes(need)).length;
  }

  renderResults() {
    const list = this.el('list');
    const ranked = this.items
      .map((item) => ({ item, score: this.score(item) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.item.order - b.item.order);

    this.items.forEach((item) => (item.hidden = true));
    ranked.forEach(({ item, score }, index) => {
      list.append(item);
      item.hidden = index >= this.limit;
      item.querySelector('[data-finder-match]').textContent =
        `${Math.round((score / this.selected.length) * 100)}% match`;
    });

    this.el('count').textContent = `${ranked.length} ${ranked.length === 1 ? 'result' : 'results'}`;
    this.el('empty').hidden = ranked.length > 0;
    this.el('more').hidden = ranked.length <= this.limit;
  }

  renderChips() {
    const chips = this.el('chips');
    chips.replaceChildren(
      ...this.selected.map((need) => {
        const tile = this.tiles.find((candidate) => candidate.dataset.need === need);
        const chip = document.createElement('span');
        chip.className = 'haya-finder__chip';
        chip.textContent = tile.querySelector('.haya-need__title').textContent;
        return chip;
      })
    );
  }

  syncUrl() {
    const params = new URLSearchParams(window.location.search);
    const set = (key, value) => (value ? params.set(key, value) : params.delete(key));
    set('needs', this.selected.join(','));
    this.filters.forEach((filter) => set(filter.dataset.finderFilter, filter.checked ? '1' : ''));
    set('results', this.el('results').hidden ? '' : '1');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }

  restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const known = new Set(this.tiles.map((tile) => tile.dataset.need));
    this.selected = (params.get('needs') || '')
      .split(',')
      .filter((need) => known.has(need))
      .slice(0, MAX_NEEDS);

    this.filters.forEach((filter) => (filter.checked = params.get(filter.dataset.finderFilter) === '1'));
    this.syncPicker();
    if (this.selected.length && params.get('results') === '1') this.showResults(false);
  }
}

if (!customElements.get('haya-need-finder')) customElements.define('haya-need-finder', HayaNeedFinder);
